import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getRecaptchaToken, loadRecaptcha } from '../lib/recaptcha';

// Safe analytics imports - wrapped to never break auth
let trackSignupCompleted = () => {};
let trackLoginCompleted = () => {};
let trackTrialStarted = () => {};
try {
  const analytics = require('../utils/analytics');
  trackSignupCompleted = analytics.trackSignupCompleted || (() => {});
  trackLoginCompleted = analytics.trackLoginCompleted || (() => {});
  trackTrialStarted = analytics.trackTrialStarted || (() => {});
} catch (e) {
  console.debug('[Auth] Analytics not available:', e);
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inactivity timeout in milliseconds (30 minutes)
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
// Warning before logout (1 minute before)
const WARNING_BEFORE_LOGOUT = 60 * 1000;

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [authCheckComplete, setAuthCheckComplete] = useState(false); // True only when auth status is definitively known
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [secondsUntilLogout, setSecondsUntilLogout] = useState(60);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Preload reCAPTCHA script
  useEffect(() => {
    loadRecaptcha().catch(() => {});
  }, []);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Logout function
  const logout = useCallback((reason = 'manual') => {
    clearAllTimers();
    setShowInactivityWarning(false);
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setAuthCheckComplete(true); // After logout, auth status is definitively known
    // Dispatch event to notify subscription context
    window.dispatchEvent(new CustomEvent('auth-change', { detail: { type: 'logout', reason } }));
  }, [clearAllTimers]);

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    if (!token) return;
    
    // Clear existing timers
    clearAllTimers();
    setShowInactivityWarning(false);
    
    // Update last activity timestamp
    localStorage.setItem('lastActivity', Date.now().toString());
    
    // Set warning timer (fires 1 minute before logout)
    warningTimerRef.current = setTimeout(() => {
      setShowInactivityWarning(true);
      setSecondsUntilLogout(60);
      
      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setSecondsUntilLogout(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);
    
    // Set logout timer
    inactivityTimerRef.current = setTimeout(() => {
      logout('inactivity');
    }, INACTIVITY_TIMEOUT);
  }, [token, clearAllTimers, logout]);

  // Check for existing session on mount
  useEffect(() => {
    if (token) {
      const lastActivity = localStorage.getItem('lastActivity');
      if (lastActivity) {
        const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
        if (timeSinceActivity >= INACTIVITY_TIMEOUT) {
          // Session expired due to inactivity
          logout('inactivity');
          setAuthCheckComplete(true);
          return;
        }
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      // No token = definitively not authenticated
      setAuthCheckComplete(true);
      setLoading(false);
    }
  }, [token]);

  // Set up activity listeners
  useEffect(() => {
    if (!user) return;
    
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = (e) => {
      // IMPORTANT: Don't treat activity inside the inactivity warning dialog as "user activity"
      // This prevents the dialog from closing when user tries to click "Log Out Now"
      // Also check if e.target is an Element with the closest method
      if (e.target && typeof e.target.closest === 'function' && e.target.closest('[role="alertdialog"]')) {
        return;
      }
      resetInactivityTimer();
    };
    
    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Initialize timer
    resetInactivityTimer();
    
    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [user, resetInactivityTimer, clearAllTimers]);

  const fetchUser = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setAuthCheckComplete(true); // Definitively authenticated
    } catch (error) {
      const status = error.response?.status;
      
      // Only logout on explicit auth failures (401/403)
      if (status === 401 || status === 403) {
        logout('session_expired');
        setAuthCheckComplete(true); // Definitively NOT authenticated
      } else if (retryCount < 1) {
        // Network/CORS/other errors: retry once after a short delay
        console.debug('[Auth] fetchUser failed, retrying...', error.message);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchUser(retryCount + 1);
      } else {
        // After retry, keep the token but don't mark auth as complete
        // This prevents guards from redirecting on temporary network issues
        console.error('[Auth] fetchUser failed after retry:', error.message);
        // Keep token, don't set authCheckComplete - user might recover on next action
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, otpCode = null) => {
    try {
      // Build login payload
      const payload = { email, password };
      
      // If CAPTCHA is required, get a token
      if (captchaRequired) {
        const recaptchaToken = await getRecaptchaToken('login');
        if (recaptchaToken) {
          payload.recaptchaToken = recaptchaToken;
        }
      }
      
      // If OTP is required, include it
      if (otpCode) {
        payload.otpCode = otpCode;
      }
      
      const response = await axios.post(`${API}/auth/login`, payload);
      const { token: newToken, user: userData } = response.data;
      
      // Reset all security states on successful login
      setCaptchaRequired(false);
      setFailedAttempts(0);
      setOtpRequired(false);
      setOtpEmail('');
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('lastActivity', Date.now().toString());
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      setAuthCheckComplete(true); // Definitively authenticated after login
      // Track login event
      trackLoginCompleted('email');
      // Dispatch event to notify subscription context
      window.dispatchEvent(new CustomEvent('auth-change', { detail: { type: 'login' } }));
      return userData;
    } catch (error) {
      // Check if CAPTCHA is required
      if (error.response?.status === 400 && error.response?.headers?.['x-captcha-required']) {
        setCaptchaRequired(true);
      }
      // Check if OTP is required
      if (error.response?.status === 400 && error.response?.headers?.['x-otp-required']) {
        setOtpRequired(true);
        setOtpEmail(email);
      }
      // Track failed attempts
      if (error.response?.status === 401) {
        setFailedAttempts(prev => prev + 1);
        if (failedAttempts + 1 >= 3) {
          setCaptchaRequired(true);
        }
      }
      throw error;
    }
  };

  // Request OTP code
  const requestOtp = async (email, purpose = 'login') => {
    try {
      await axios.post(`${API}/auth/request-otp`, { email, purpose });
      setOtpRequired(true);
      setOtpEmail(email);
      return true;
    } catch (error) {
      throw error;
    }
  };

  // Verify OTP code
  const verifyOtp = async (email, code, purpose = 'login') => {
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { email, code, purpose });
      return response.data.verified;
    } catch (error) {
      throw error;
    }
  };

  // Check if CAPTCHA is required for an email
  const checkCaptchaRequired = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/check-captcha?email=${encodeURIComponent(email)}`);
      setCaptchaRequired(response.data.captcha_required);
      setFailedAttempts(response.data.failed_attempts || 0);
      return response.data.captcha_required;
    } catch {
      return false;
    }
  };

  const register = async (email, password, name) => {
    // Get reCAPTCHA token for signup protection
    const recaptchaToken = await getRecaptchaToken('register');
    
    const payload = { email, password, name };
    if (recaptchaToken) {
      payload.recaptchaToken = recaptchaToken;
    }
    
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('lastActivity', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setAuthCheckComplete(true); // Definitively authenticated after register
    // Track signup and trial started events
    trackSignupCompleted('email');
    trackTrialStarted();
    // Dispatch event to notify subscription context
    window.dispatchEvent(new CustomEvent('auth-change', { detail: { type: 'register' } }));
    return { user: userData, token: newToken };
  };

  // Stay logged in (dismiss warning and reset timer)
  const stayLoggedIn = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Set auth data directly (for OAuth callbacks)
  const setAuthData = useCallback((newToken, userData) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('lastActivity', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setAuthCheckComplete(true); // Definitively authenticated after OAuth
    window.dispatchEvent(new CustomEvent('auth-change', { detail: { type: 'oauth' } }));
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout: () => logout('manual'), 
      // isAuthenticated is true only when we have a confirmed user
      // Page guards should use: if (!loading && authCheckComplete && !isAuthenticated) redirect
      isAuthenticated: !!user,
      // authCheckComplete is true when we definitively know the auth status
      // (successful login/register, 401/403 rejection, or no token)
      // It's false during loading or if network errors prevented verification
      authCheckComplete,
      showInactivityWarning,
      secondsUntilLogout,
      stayLoggedIn,
      captchaRequired,
      failedAttempts,
      checkCaptchaRequired,
      otpRequired,
      otpEmail,
      requestOtp,
      verifyOtp,
      setAuthData
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
